import type { ComposerTranslation } from 'vue-i18n'
import { listCol } from '@/utils/list-columns-i18n'
import { formatMapToOptions } from '@/utils/tool'

export enum ProtectionType {
  // 诱饵文件篡改
  DecoyFileTampering = 1,
  // 其他文件篡改
  OtherFileTampering = 2,
  // 主导记录（MBR）篡改
  DominantRecord = 3,
  // 卷影删除
  Tamper = 4,
  // 暴力破解
  BruteForceCracking = 5,
  // 漏洞利用
  Exploit = 6,
  // 勒索特征识别
  RansomwareSignatureIdentification = 7,
}
export type ProtectionTypeKeys = keyof typeof ProtectionType
export type Values = typeof ProtectionType[ProtectionTypeKeys]

const PROTECTION_VALUES: ReadonlyArray<Values> = [1, 2, 3, 4, 5, 6, 7]

/** 勒索风险类型短标签 */
export function trProtectionType(t: ComposerTranslation, v: Values): string {
  return t(`ransomwareIncident.protectionType.${v}`)
}

/** 事件描述（长文案） */
export function trProtectionDesc(t: ComposerTranslation, v: number): string {
  if (!v || v < 1 || v > 7) return t('ransomwareIncident.unknown')
  return t(`ransomwareIncident.protectionDesc.${v}`)
}

export function protectionTypeOptions(t: ComposerTranslation) {
  const map: Record<string, string> = {}
  for (const v of PROTECTION_VALUES) {
    map[String(v)] = trProtectionType(t, v)
  }
  return formatMapToOptions(map, true)
}

// export protectionTypeDictData = Object.entries().reduce((p,c,i) => (), [])
export enum Status {
  // 全部
  // All = 0,
  // 允许
  UnAllowed = 0,
  // 阻止
  Allowed = 1,
  // 阻止并结束
  UnAllowedAndEnd = 2,
  // 仅记录
  Record = 3
}
export type StatusKeys = keyof typeof Status
export type StatusValues = typeof Status[StatusKeys]

const STATUS_VALUES: ReadonlyArray<StatusValues> = [0, 1, 2, 3]

/** 列表筛选项：不含「阻止并结束」(2)，该项仅由/统计卡通过 statuses 含 2 查询 */
const STATUS_FILTER_VALUES: ReadonlyArray<StatusValues> = [0, 1, 3]

export function trBlockStatus(t: ComposerTranslation, v: number): string {
  return t(`ransomwareIncident.blockStatus.${v}`)
}

export function blockStatusOptions(t: ComposerTranslation) {
  const map: Record<string, string> = {}
  for (const v of STATUS_VALUES) {
    map[String(v)] = trBlockStatus(t, v)
  }
  return formatMapToOptions(map, true)
}

/** 勒索事件列表「处理状态」下拉：排除 2（阻止并结束），与接口 statuses 语义区分 */
export function blockStatusFilterOptions(t: ComposerTranslation) {
  const map: Record<string, string> = {}
  for (const v of STATUS_FILTER_VALUES) {
    map[String(v)] = trBlockStatus(t, v)
  }
  return formatMapToOptions(map, true)
}

export enum Level {
  // 高危
  Heigh = 4,
  // 中危
  Middle = 3,
  // 低危
  Low = 2,
  // 未知
  Default = 1
}
export type LevelKeys = keyof typeof Level
export type LevelValues = typeof Level[LevelKeys]

const LEVEL_VALUES: ReadonlyArray<LevelValues> = [1, 2, 3, 4]

export function trRiskLevel(t: ComposerTranslation, v: LevelValues): string {
  return t(`ransomwareIncident.riskLevel.${v}`)
}

export function riskLevelOptions(t: ComposerTranslation) {
  const map: Record<string, string> = {}
  for (const v of LEVEL_VALUES) {
    map[String(v)] = trRiskLevel(t, v)
  }
  return formatMapToOptions(map, true)
}

/** 终端公共列：按需包含；未指定的键默认为 true（全量列） */
export type PartialListInclude = Partial<{
  computer: boolean
  group: boolean
  userMark: boolean
  ip: boolean
  mac: boolean
}>

export type PartialListColumn = {
  label: string
  prop: string
  showOverflow?: boolean
  type?: string
  minWidth?: number | string
  columnOpt?: Record<string, unknown>
  colOpt?: Record<string, unknown>
}

type PartialListKey = keyof Required<PartialListInclude>

const PARTIAL_LIST_ORDER: PartialListKey[] = ['computer', 'group', 'userMark', 'ip', 'mac']

type PartialListBuildCtx = {
  t: ComposerTranslation
  hideComputerIcon: boolean
  propsFlag?: boolean
}

const PARTIAL_LIST_BUILDERS: Record<
  PartialListKey,
  (ctx: PartialListBuildCtx) => PartialListColumn
> = {
  computer: ({ t, hideComputerIcon }) => ({
    label: listCol(t, 'computerName'),
    prop: 'computer_name',
    showOverflow: true,
    type: !hideComputerIcon ? 'COMPUTER' : '',
    minWidth: 160,
  }),
  group: ({ t, }) => ({
    label: listCol(t, 'groupName'),
    prop: 'group_name',
    minWidth:160
  }),
  userMark: ({ t, }) => ({
    label: listCol(t, 'username'),
    prop: 'user_mark',
    showOverflow: true, minWidth: 140
  }),
  ip: ({ t }) => ({
    label: listCol(t, 'ipAddress'),
    prop: 'system_ip',
    type: 'IP',
    minWidth: 180,
  }),
  mac: ({ t, }) => ({
    label: listCol(t, 'macAddress'),
    prop: 'mac',
    showOverflow: true,
    minWidth: 180
  }),
}

/**
 * @param hideComputerIcon true： 显示COMPUTER 内置列类型；
 * @param include 为 false 的列不输出；缺省全部输出
 */
export function computerInfoCols(
  hideComputerIcon: boolean,
  propsFlag: boolean,
  t: ComposerTranslation,
  include?: PartialListInclude,
): PartialListColumn[] {
  const inc: Required<PartialListInclude> = {
    computer: true,
    group: true,
    userMark: true,
    ip: true,
    mac: true,
    ...include,
  }
  const ctx: PartialListBuildCtx = { t, hideComputerIcon, propsFlag }
  return PARTIAL_LIST_ORDER.filter((key) => inc[key]).map((key) => PARTIAL_LIST_BUILDERS[key](ctx))
}
